import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const getIntegersAndDecimals = (input: string) => input.split('.');

export function getCommaSeparatedNumber(inputValue: string) {
  const [integerPart, decimalPart] = getIntegersAndDecimals(inputValue);
  const formattedIntegerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return decimalPart ? `${formattedIntegerPart}.${decimalPart}` : formattedIntegerPart;
}

// Capped at 13 integer digits (~$9.99 trillion) to preserve cent-level precision.
// IEEE 754 doubles lose 0.01 accuracy above ~$45 trillion, so values beyond 13
// integer digits would silently drop decimal places when saved.
export const PRICE_MAX_VALUE = 9_999_999_999_999;
const PRICE_MIN_VALUE = -PRICE_MAX_VALUE;
const MAX_INTEGER_DIGITS = 13;
const MAX_DECIMAL_DIGITS = 2;

function toDisplayString(num: number): string {
  return getCommaSeparatedNumber(num.toFixed(MAX_DECIMAL_DIGITS));
}

function validateRaw(raw: string, t: (k: string, o?: object) => string): string | null {
  const cleaned = raw.replace(/,/g, '');
  if (cleaned === '' || cleaned === '-') return null;

  const isNegative = cleaned.startsWith('-');
  const abs = isNegative ? cleaned.slice(1) : cleaned;
  const [intPart] = getIntegersAndDecimals(abs);

  if (intPart.length > MAX_INTEGER_DIGITS) {
    return isNegative
      ? t('editor.error_messages.under_min_value', { minValue: PRICE_MIN_VALUE })
      : t('editor.error_messages.over_max_value', { maxValue: PRICE_MAX_VALUE });
  }

  const num = parseFloat(cleaned);
  if (isNaN(num)) return t('editor.error_messages.invalid_number');
  if (num > PRICE_MAX_VALUE && !isNegative) {
    return t('editor.error_messages.over_max_value', { maxValue: PRICE_MAX_VALUE });
  }
  if (num < PRICE_MIN_VALUE) {
    return t('editor.error_messages.under_min_value', { minValue: PRICE_MIN_VALUE });
  }

  return null;
}

export const useFieldValidator = () => {
  const { t } = useTranslation();

  const [error, setError] = useState<string | null>(null);
  const [displayValue, setDisplayValue] = useState<string>('');

  const errorRef = useRef<string | null>(null);
  const focusedRef = useRef(false);
  const lastValidRef = useRef<number>(0);

  const setErrorSync = (err: string | null) => {
    errorRef.current = err;
    setError(err);
  };

  const setExternalValue = useCallback(
    (num: number) => {
      if (focusedRef.current) return;
      if (isNaN(num)) return;

      lastValidRef.current = num;
      const err = validateRaw(String(num), t);
      setDisplayValue(toDisplayString(num));
      setErrorSync(err);
    },
    [t]
  );

  const handleChange = useCallback(
    (raw: string): number | null => {
      const cleaned = raw.replace(/,/g, '');

      if (cleaned !== '' && cleaned !== '-' && !/^-?\d*\.?\d*$/.test(cleaned)) {
        return null;
      }

      setDisplayValue(raw);

      const err = validateRaw(raw, t);
      setErrorSync(err);

      if (err !== null) return null;

      const num = parseFloat(cleaned);
      return isNaN(num) ? null : num;
    },
    [t]
  );

  const handleFocus = useCallback(() => {
    focusedRef.current = true;
    setDisplayValue((prev) => prev.replace(/,/g, ''));
  }, []);

  const handleBlur = useCallback((): number => {
    focusedRef.current = false;

    const cleaned = displayValue.replace(/,/g, '');
    const err = validateRaw(displayValue, t);

    if (err !== null) {
      return NaN;
    }

    if (cleaned === '' || cleaned === '-' || parseFloat(cleaned) === 0) {
      setDisplayValue(toDisplayString(lastValidRef.current));
      setErrorSync(null);
      return NaN;
    }

    const num = parseFloat(cleaned);
    lastValidRef.current = num;
    setDisplayValue(toDisplayString(num));
    setErrorSync(null);
    return num;
  }, [displayValue, t]);

  return {
    error,
    errorRef,
    displayValue,
    setExternalValue,
    handleChange,
    handleFocus,
    handleBlur,
  };
};
